import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import Breadcrumb from '@/components/ui/Breadcrumb'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { ArticleContent } from '@/components/ui/ArticleContent'
import {
  Facebook,
  Twitter,
  Linkedin,
  Link as LinkIcon,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react'
import {
  usePressRelease,
  usePublishedPressReleases,
  usePressReleaseComments,
  useAddPressReleaseComment,
  useIncrementPressReleaseView,
  usePressReleaseReplies
} from '@/lib/api/hooks/press-release'
import { motion } from 'framer-motion'
import { fadeUp, staggerContainer } from '@/lib/motion'
import { useFilterEvents } from '@/lib/api/hooks/event'

const STORAGE_KEY = 'press-release-comment-user-info'

// Component to display a comment with its replies (read-only)
interface CommentWithRepliesProps {
  comment: {
    id: string
    name: string
    dateCommented: string
    comment: string
  }
  formatCommentDate: (date: string) => string
}

function CommentWithReplies({
  comment,
  formatCommentDate
}: CommentWithRepliesProps) {
  const { data: repliesData } = usePressReleaseReplies(comment.id)
  const replies = (repliesData?.data || []) as Array<{
    id: string
    name: string
    dateReplied: string
    reply: string
  }>

  return (
    <div className="flex flex-col gap-4 pb-6 border-b border-gray-200">
      {/* Comment */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <span className="text-base font-semibold text-gray-900">{comment.name}</span>
          <span className="text-sm text-gray-500">
            {formatCommentDate(comment.dateCommented)}
          </span>
        </div>
        <p className="text-base text-gray-700 leading-relaxed">{comment.comment}</p>
      </div>

      {/* Replies (from admin only) */}
      {replies.length > 0 && (
        <div className="ml-8 flex flex-col gap-4">
          {replies.map((reply) => (
            <div key={reply.id} className="flex flex-col gap-1 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-900">{reply.name}</span>
                <span className="text-xs text-gray-500">
                  {formatCommentDate(reply.dateReplied)}
                </span>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{reply.reply}</p>
            </div>
          ))}
        </div>
      )}


    </div>
  )
}

export default function PressReleaseArticle() {
  const { articleId } = useParams()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [selectedAdditionalImageIndex, setSelectedAdditionalImageIndex] = useState<number | null>(null)

  // Load saved user info from localStorage
  const [commentForm, setCommentForm] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      try {
        const { name, email } = JSON.parse(saved)
        return { name, email, comment: '', saveInfo: true }
      } catch {
        return { name: '', email: '', comment: '', saveInfo: false }
      }
    }
    return { name: '', email: '', comment: '', saveInfo: false }
  })
  const [commentError, setCommentError] = useState('')
  const [commentSuccess, setCommentSuccess] = useState(false)


  const { data: articleData, isLoading } = usePressRelease(articleId)
  const { data: otherArticlesData } = usePublishedPressReleases({ page: 1, size: 10 }, {})
  const { data: commentsData } = usePressReleaseComments(articleId)
  const addCommentMutation = useAddPressReleaseComment()
  const { mutate: incrementView } = useIncrementPressReleaseView()

  // Extract article from API response
  const apiArticle = articleData?.data
  const allArticles = otherArticlesData?.data?.data || []
  const comments = commentsData?.data || []

  // Find current article index in the list for navigation
  const currentArticleIndex = allArticles.findIndex(pr => pr.pressReleaseId === articleId)
  const hasPrevious = currentArticleIndex > 0
  const hasNext = currentArticleIndex >= 0 && currentArticleIndex < allArticles.length - 1
  const previousArticle = hasPrevious ? allArticles[currentArticleIndex - 1] : null
  const nextArticle = hasNext ? allArticles[currentArticleIndex + 1] : null

  // Get other articles for sidebar (excluding current article)
  const otherArticles = allArticles
    .filter(pr => pr.pressReleaseId !== articleId)
    .slice(0, 4)
    .map((pr) => ({
      id: pr.pressReleaseId,
      title: pr.title,
      date: pr.datePublished
        ? new Date(pr.datePublished).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: '2-digit' }).replace(/,/g, " '")
        : new Date(pr.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: '2-digit' }).replace(/,/g, " '"),
      image: pr.coverImage
    }))

  // Increment view count when article is loaded (only once per article)
  useEffect(() => {
    if (articleId) {
      incrementView(articleId)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId])

  // Format date helper
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  }

  const formatCommentDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  }

  // Article data from API
  const article = apiArticle ? {
    id: articleId || '',
    title: apiArticle.title,
    heroImage: apiArticle.coverImage,
    date: apiArticle.datePublished ? formatDate(apiArticle.datePublished) : formatDate(apiArticle.createdAt),
    altText: apiArticle.coverImageTitle || apiArticle.title,
    content: apiArticle.content || '',
    articleTag: apiArticle.articleTag || [],
    additionalImageUrls: apiArticle.additionalImageUrls || [],
    additionalFileUrls: apiArticle.additionalFileUrls || []
  } : null

  const getFileNameFromUrl = (url: string): string => {
    const cleaned = url.split('?')[0].split('#')[0]
    const name = cleaned.split('/').pop()
    return name && name.trim() ? decodeURIComponent(name) : 'document'
  }

  const openAdditionalImage = (index: number) => setSelectedAdditionalImageIndex(index)
  const closeAdditionalImage = () => setSelectedAdditionalImageIndex(null)
  const showPrevAdditionalImage = () => {
    if (!article?.additionalImageUrls?.length || selectedAdditionalImageIndex === null) return
    const nextIndex = (selectedAdditionalImageIndex - 1 + article.additionalImageUrls.length) % article.additionalImageUrls.length
    setSelectedAdditionalImageIndex(nextIndex)
  }
  const showNextAdditionalImage = () => {
    if (!article?.additionalImageUrls?.length || selectedAdditionalImageIndex === null) return
    const nextIndex = (selectedAdditionalImageIndex + 1) % article.additionalImageUrls.length
    setSelectedAdditionalImageIndex(nextIndex)
  }

  // Get related articles (excluding current article)
  const relatedArticles = allArticles
    .filter(pr => pr.pressReleaseId !== articleId)
    .slice(0, 2)
    .map((pr) => ({
      id: pr.pressReleaseId,
      title: pr.title,
      image: pr.coverImage
    }))

    const { data: upcomingEventsData } = useFilterEvents(
        { page: 1, size: 5 },
        { upcoming: true }
      )
    

  const upcomingEvents = upcomingEventsData?.data?.data ? upcomingEventsData.data.data.map((event) => ({
    id: event.eventId,
    title: event.eventTitle,
    date: event.startDate ? formatDate(event.startDate) : '',
    image: event.imageUrl || ''
  })) : []


  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setCommentError('')
    setCommentSuccess(false)

    if (!articleId) {
      setCommentError('Article ID is missing')
      return
    }

    try {
      await addCommentMutation.mutateAsync({
        pressReleaseId: articleId,
        data: {
          name: commentForm.name,
          email: commentForm.email,
          comment: commentForm.comment
        }
      })

      // Save or clear user info from localStorage
      if (commentForm.saveInfo) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          name: commentForm.name,
          email: commentForm.email
        }))
      } else {
        localStorage.removeItem(STORAGE_KEY)
      }

      // Reset form
      setCommentForm({
        name: commentForm.saveInfo ? commentForm.name : '',
        email: commentForm.saveInfo ? commentForm.email : '',
        comment: '',
        saveInfo: commentForm.saveInfo
      })
      setCommentSuccess(true)

      // Clear success message after 3 seconds
      setTimeout(() => setCommentSuccess(false), 3000)
    } catch (error) {
      setCommentError('Failed to submit comment. Please try again.')
      console.error('Error submitting comment:', error)
    }
  }


  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setEmail('')
  }

  const handleShare = (platform: string) => {
    if (!article) return

    const url = window.location.href
    const title = article.title

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank')
        break
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, '_blank')
        break
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank')
        break
      case 'copy':
        navigator.clipboard.writeText(url)
        break
    }
  }

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Press Release', href: '/media/press-release' },
    { label: article?.title || 'Loading...', href: '', active: true }
  ]

  // Show loading state
  if (isLoading) {
    return <LoadingSpinner fullScreen size="lg" message="Loading article..." />
  }

  // Show error state if article not found
  if (!article) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Article not found</p>
          <Link to="/media/press-release" className="text-wine-100 hover:underline mt-4 inline-block">
            Back to Press Releases
          </Link>
        </div>
      </div>
    )
  }

  return (
    <motion.div
      className="min-h-screen bg-white space-y-4"
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
    >
      {/* Breadcrumb */}
      <motion.div className="px-4 md:px-20" variants={fadeUp}>
        <Breadcrumb items={breadcrumbItems} />
      </motion.div>

      {/* Main Content */}
      <motion.div className="px-4 md:px-20 pb-16" variants={fadeUp}>
        <motion.div
          className="max-w-[1268px] mx-auto"
          variants={fadeUp}
        >
          {/* Article Header */}
          <div className="flex flex-col gap-8 mb-16">
            <div className="flex flex-col gap-4">
              <h1 className="text-2xl md:text-4xl font-normal font-['PT_Serif'] leading-tight text-gray-900">
                {article.title}
              </h1>
              {article.date && (
                <p className="text-base font-medium text-gray-500">
                  {article.date}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <img
                src={article.heroImage}
                alt={article.altText}
                className="w-full h-[300px] md:h-[502px] object-cover"
              />
              {/* {article.altText && (
                <p className="text-base font-medium text-gray-500">
                  {article.altText}
                </p>
              )} */}
            </div>
          </div>

          {/* Content and Sidebar */}
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Main Article Content */}
            <div className="flex-1 lg:w-[804px]">
              {/* Article Content */}
              <div className="flex flex-col gap-24 mb-24">
                <div className="flex flex-col gap-12">
                  <div className="flex flex-col gap-10">
                    <ArticleContent content={article.content} className="" />

                    {(article.additionalImageUrls?.length > 0 || article.additionalFileUrls?.length > 0) && (
                      <div className="flex flex-col gap-6">
                        {article.additionalImageUrls?.length > 0 && (
                          <div className="flex flex-col gap-3">
                            <h3 className="text-xl font-normal font-['PT_Serif'] text-gray-900">ADDITIONAL IMAGES</h3>
                            <div className="flex gap-3 overflow-x-auto pb-2">
                              {article.additionalImageUrls.map((url: string, index: number) => (
                                <button
                                  key={`${url}-${index}`}
                                  type="button"
                                  onClick={() => openAdditionalImage(index)}
                                  className="shrink-0 rounded-lg overflow-hidden border border-gray-200 hover:border-wine-100 transition-colors"
                                  aria-label={`Open additional image ${index + 1}`}
                                >
                                  <img src={url} alt={`Additional image ${index + 1}`} className="w-36 h-24 object-cover" />
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {article.additionalFileUrls?.length > 0 && (
                          <div className="flex flex-col gap-3">
                            <h3 className="text-xl font-normal font-['PT_Serif'] text-gray-900">ADDITIONAL FILES</h3>
                            <ul className="flex flex-col gap-2">
                              {article.additionalFileUrls.map((url: string, index: number) => (
                                <li key={`${url}-${index}`}>
                                  <a
                                    href={url}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    className="text-base text-wine-100 hover:text-wine-200 hover:underline break-all"
                                  >
                                    {getFileNameFromUrl(url)}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Article Tags */}
                    {article.articleTag && article.articleTag.length > 0 && (
                      <div className="flex flex-wrap gap-4">
                        {article.articleTag.map((tag, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-imageBg-100 rounded-full text-sm font-medium text-forestGreen-100 uppercase"
                          >
                            {tag.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Social Share */}
                  <div className="flex items-center gap-4">
                    <span className="text-lg font-normal text-gray-500 font-['PT_Serif']">SHARE</span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleShare('facebook')}
                        className="w-6 h-6 hover:opacity-70"
                      >
                        <Facebook className="w-5 h-5 text-gray-800" />
                      </button>
                      <button
                        onClick={() => handleShare('twitter')}
                        className="w-6 h-6 hover:opacity-70"
                      >
                        <Twitter className="w-5 h-5 text-gray-800" />
                      </button>
                      <button
                        onClick={() => handleShare('linkedin')}
                        className="w-6 h-6 hover:opacity-70"
                      >
                        <Linkedin className="w-5 h-5 text-gray-800" />
                      </button>
                      <button
                        onClick={() => handleShare('copy')}
                        className="w-6 h-6 hover:opacity-70"
                      >
                        <LinkIcon className="w-5 h-5 text-gray-800" />
                      </button>
                    </div>
                  </div>

                  {/* Navigation Buttons */}
                  {!!(hasPrevious || hasNext) && (
                    <div className="flex justify-between items-center">
                      {hasPrevious && previousArticle && (
                        <button
                          onClick={() => navigate(`/media/press-release/${previousArticle.pressReleaseId}`)}
                          className="px-3 py-2 bg-white rounded-lg border border-gray-300 flex items-center gap-2 hover:bg-gray-50"
                        >
                          <ChevronLeft className="w-4 h-4 text-gray-900" />
                          <span className="text-base font-medium text-gray-900">Previous</span>
                        </button>
                      )}
                      {hasNext && nextArticle && (
                        <button
                          onClick={() => navigate(`/media/press-release/${nextArticle.pressReleaseId}`)}
                          className="px-3 py-2 bg-white rounded-lg border border-gray-300 flex items-center gap-2 hover:bg-gray-50 ml-auto"
                        >
                          <span className="text-base font-medium text-gray-900">Next</span>
                          <ChevronRight className="w-4 h-4 text-gray-900" />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Related Articles */}
                {relatedArticles.length > 0 && (
                  <div className="flex flex-col gap-8">
                    <h2 className="text-2xl font-normal font-['PT_Serif'] text-gray-900">RELATED ARTICLES</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                      {relatedArticles.map((relatedArticle) => (
                        <Link key={relatedArticle.id} to={`/media/press-release/${relatedArticle.id}`} className="block">
                          <div className="flex flex-col gap-4">
                            <img
                              src={relatedArticle.image}
                              alt={relatedArticle.title}
                              className="w-full h-48 object-cover"
                            />
                            <h3 className="text-lg font-medium leading-relaxed text-gray-900">
                              {relatedArticle.title}
                            </h3>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Comments Section */}
                {apiArticle?.allowComment !== false && (
                  <div className="flex flex-col gap-8">
                    {/* Display Existing Comments */}
                    {comments.length > 0 && (
                      <div className="flex flex-col gap-6">
                        <h2 className="text-2xl font-normal font-['PT_Serif'] text-gray-900">
                          COMMENTS ({comments.length})
                        </h2>
                        <div className="flex flex-col gap-6">
                          {comments.map((comment) => (
                            <CommentWithReplies
                              key={comment.id}
                              comment={comment}
                              formatCommentDate={formatCommentDate}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Leave a Comment Form */}
                    <div className="flex flex-col gap-8">
                      <div className="flex flex-col gap-4">
                        <h2 className="text-2xl font-normal font-['PT_Serif'] text-gray-900">LEAVE A COMMENT</h2>
                        <p className="text-base text-gray-900">Your email address will not be published</p>
                      </div>

                      {commentError && (
                        <div className="px-4 py-2 bg-red-50 border border-red-200 rounded text-red-700">
                          {commentError}
                        </div>
                      )}

                      {commentSuccess && (
                        <div className="px-4 py-2 bg-green-50 border border-green-200 rounded text-green-700">
                          Comment submitted successfully!
                        </div>
                      )}

                      <form onSubmit={handleCommentSubmit} className="flex flex-col gap-4">
                        <div className="flex flex-col gap-1.5">
                          <label className="text-base text-gray-900">Comment</label>
                          <textarea
                            value={commentForm.comment}
                            onChange={(e) => setCommentForm({ ...commentForm, comment: e.target.value })}
                            className="w-full h-80 bg-gray-50 rounded p-4 resize-none"
                            required
                            disabled={addCommentMutation.isPending}
                          />
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="flex flex-col gap-1.5">
                            <label className="text-base text-gray-900">Name</label>
                            <input
                              type="text"
                              value={commentForm.name}
                              onChange={(e) => setCommentForm({ ...commentForm, name: e.target.value })}
                              className="h-12 bg-gray-50 rounded px-4"
                              placeholder="Your name"
                              required
                              disabled={addCommentMutation.isPending}
                            />
                          </div>
                          <div className="flex flex-col gap-1.5">
                            <label className="text-base text-gray-900">Email Address</label>
                            <input
                              type="email"
                              value={commentForm.email}
                              onChange={(e) => setCommentForm({ ...commentForm, email: e.target.value })}
                              className="h-12 bg-gray-50 rounded px-4"
                              placeholder="johndoe@email.com"
                              required
                              disabled={addCommentMutation.isPending}
                            />
                          </div>
                        </div>

                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="saveInfo"
                            checked={commentForm.saveInfo}
                            onChange={(e) => setCommentForm({ ...commentForm, saveInfo: e.target.checked })}
                            className="mr-2 w-4 h-4 accent-wine-100 cursor-pointer"
                            disabled={addCommentMutation.isPending}
                          />
                          <label htmlFor="saveInfo" className="text-base text-gray-900 cursor-pointer">
                            Save my name and email in this browser for the next time I comment.
                          </label>
                        </div>

                        <button
                          type="submit"
                          disabled={addCommentMutation.isPending}
                          className="px-8 py-2.5 bg-wine-100 text-white rounded font-bold text-base hover:bg-wine-200 self-start disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {addCommentMutation.isPending ? 'Posting...' : 'Post Comment'}
                        </button>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="w-full lg:w-96 flex flex-col gap-6">
              {/* Other Articles */}
              {otherArticles.length > 0 && (
                <div className="flex flex-col gap-6">
                  <h2 className="text-2xl font-normal font-['PT_Serif'] text-gray-900">OTHER ARTICLES</h2>
                  <div className="flex flex-col">
                    {otherArticles.map((otherArticle) => (
                      <Link
                        key={otherArticle.id}
                        to={`/media/press-release/${otherArticle.id}`}
                        className="block p-2 hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <div className="flex flex-col gap-2">
                          <span className="text-sm font-medium text-gray-500">{otherArticle.date}</span>
                          <div className="flex gap-4">
                            <img
                              src={otherArticle.image}
                              alt={otherArticle.title}
                              className="w-24 h-24 object-cover"
                            />
                            <div className="flex-1">
                              <h3 className="text-lg leading-relaxed text-gray-900 font-['PT_Serif']">
                                {otherArticle.title}
                              </h3>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Upcoming Events */}
              <div className="flex flex-col gap-6">
                <h2 className="text-2xl font-normal font-['PT_Serif'] text-gray-900">UPCOMING EVENTS</h2>
                <div className="flex flex-col gap-8">
                  {Array.isArray(upcomingEvents) && upcomingEvents.length > 0 ?  upcomingEvents.map((event) => (
                    <div key={event.id} className="flex flex-col">
                      <img
                        src={event.image}
                        alt={event.title}
                        className="w-full h-44 object-cover"
                      />
                      <div className="px-4 py-6 bg-gray-50 flex flex-col gap-4">
                        <h3 className="text-lg leading-relaxed text-gray-900 font-['PT_Serif']">
                          {event.title}
                        </h3>
                        <span className="text-base font-medium text-gray-900">{event.date}</span>
                      </div>
                    </div>
                  )) : (
                    <p className="text-base">No upcoming events at the moment. Please check back later.</p>
                   )}
                </div>
              </div>

              {/* Newsletter Signup */}
              <div className="flex flex-col gap-8">
                <div className="flex flex-col gap-4">
                  <h2 className="text-2xl font-normal font-['PT_Serif'] text-gray-900">JOIN OUR NEWSLETTER</h2>
                  <p className="text-lg leading-relaxed text-gray-500">
                    Get the latest updates, insights, and exclusive content delivered straight to your inbox.
                  </p>
                </div>
                <form onSubmit={handleNewsletterSubmit} className="flex flex-col gap-1.5">
                  <label className="text-base text-gray-900">Email Address</label>
                  <div className="flex">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="flex-1 h-12 bg-gray-50 rounded-l px-4"
                      placeholder="johndoe@email.com"
                      required
                    />
                    <button
                      type="submit"
                      className="px-8 py-2.5 bg-wine-100 text-white rounded-r font-bold text-base hover:bg-wine-200"
                    >
                      SUBSCRIBE
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {selectedAdditionalImageIndex !== null && (article.additionalImageUrls?.length || 0) > 0 && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <button
            type="button"
            onClick={closeAdditionalImage}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/20 text-white hover:bg-white/30"
            aria-label="Close image viewer"
          >
            <X className="w-5 h-5" />
          </button>

          <button
            type="button"
            onClick={showPrevAdditionalImage}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/20 text-white hover:bg-white/30"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <img
            src={article.additionalImageUrls[selectedAdditionalImageIndex]}
            alt={`Additional image ${selectedAdditionalImageIndex + 1}`}
            className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
          />

          <button
            type="button"
            onClick={showNextAdditionalImage}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/20 text-white hover:bg-white/30"
            aria-label="Next image"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      )}
    </motion.div>
  )
}